import {
  type Resolver,
  type AwilixContainer,
  type ResolveOptions,
  type ContainerOptions,
  createContainer,
  asValue,
} from 'awilix';
import { type ContainerContext } from './types';
import { getLogger } from '../libs/logger';
import { useContext, type Context } from 'react';

const logger = getLogger('Container');

/**
 * Container definition base.
 */
export type ContainerDefinition = Record<string, Resolver<unknown>>;

/**
 * Extracts the type that will be resolved from a resolver.
 */
export type ExtractResolverType<T> = T extends Resolver<infer X> ? X : null;

/**
 * Strongly-typed container.
 */
export type TypedAwilixContainer<T extends ContainerDefinition> = {
  /**
   * Resolves the registration with the given name.
   *
   * @param name The name of the registration to resolve.
   *
   */
  resolve: <K extends keyof T>(
    key: K,
    resolveOptions?: ResolveOptions,
  ) => ExtractResolverType<T[K]>;

  /**
   * Registers additional services and returns a new container with the updated types.
   *
   * @param newRegistrations The new service registrations to add.
   * @returns A new container with the combined services.
   */
  register: (<R extends Record<string, Resolver<unknown>>>(
    newRegistrations: R,
  ) => TypedAwilixContainer<T & R>) &
    (<R>(
      name: string | symbol,
      registration: Resolver<R>,
    ) => TypedAwilixContainer<T & Record<typeof name, Resolver<R>>>);

  /**
   * @param name The name of the registration to resolve
   * @param registration The new service registration
   *
   * @returns A new container with the combined services
   */
  createScope: () => TypedAwilixContainer<T>;
} & Pick<
  AwilixContainer<T>,
  Exclude<keyof AwilixContainer, 'resolve' | 'register' | 'createScope'>
>;

/**
 * Wraps `createContainer` and calls `register` on it.
 */
export function createTypedContainer<T extends ContainerDefinition>(
  registrations?: T,
  opts?: ContainerOptions,
): TypedAwilixContainer<T> {
  const container = createContainer(opts) as TypedAwilixContainer<T>;

  if (registrations) {
    container.register(registrations);
  }

  return container;
}

/**
 * Creates a strictly typed scope and registers additional services.
 *
 * @param parentContainer The parent container to create the scope from.
 * @param registrations Additional service registrations.
 *
 * @returns The new scoped container.
 */
export function createScope<
  T extends Record<string, Resolver<unknown>>,
  R extends Record<string, Resolver<unknown>>,
>(
  parentContainer: TypedAwilixContainer<T>,
  registrations: R,
): TypedAwilixContainer<T & R> {
  const scope = parentContainer.createScope() as TypedAwilixContainer<T & R>;

  scope.register(registrations);

  return scope;
}

/**
 * Creates a typed `useService` hook for a given container.
 *
 * @param context The context where the service should be resolved.
 *
 * @returns A tuple containing the `useService` hook
 */
export function createUseServiceHook<T extends ContainerDefinition>(
  Context: Context<TypedAwilixContainer<T> | null>,
) {
  /**
   * Hook to retrieve a service by its key with automatic type inference.
   */
  return function useService<K extends keyof T>(
    serviceName: K,
  ): ExtractResolverType<T[K]> {
    const container = useContext(Context);

    if (!container) {
      throw new Error('Container is not available in the context');
    }

    return container.resolve(serviceName);
  };
}

export async function setContext<
  T extends { $context: Resolver<ContainerContext> },
>(
  container: TypedAwilixContainer<T>,
  context: ContainerContext,
): Promise<void> {
  if (container.resolve('$context') === context) {
    return;
  }

  logger.info('setting context', { context });

  // dispose current context
  await container.dispose();

  // re-register context
  container.register({
    $context: asValue(context),
  });
}
